import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { useAccount } from "@/context/AccountAuthContext";
import { Loader2, LogIn, UserPlus, LogOut, Mail, CalendarDays, BadgeCheck, ArrowRight, FileText, Download, Trash2, Plus, Upload } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Account() {
  const { user, login, register, googleExchange, logout, authHeader } = useAccount();
  const navigate = useNavigate();
  const [processing, setProcessing] = useState(false);

  // Google callback: exchange session_id from URL fragment
  useEffect(() => {
    const hash = window.location.hash || "";
    if (hash.includes("session_id=")) {
      const sid = new URLSearchParams(hash.replace(/^#/, "")).get("session_id");
      if (sid) {
        setProcessing(true);
        googleExchange(sid)
          .catch(() => {})
          .finally(() => {
            window.history.replaceState(null, "", "/mon-compte");
            setProcessing(false);
          });
      }
    }
  }, [googleExchange]);

  if (processing || user === null) {
    return <Shell><div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#e07a5f]" size={40} /></div></Shell>;
  }

  return <Shell>{user ? <Dashboard user={user} logout={logout} authHeader={authHeader} navigate={navigate} /> : <AuthForms login={login} register={register} />}</Shell>;
}

function Shell({ children }) {
  return (
    <div data-testid="account-page" className="min-h-screen bg-[#fbf6ec]">
      <div className="max-w-5xl mx-auto px-5 lg:px-10 pt-28 pb-16">{children}</div>
    </div>
  );
}

function googleLogin() {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/mon-compte";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

function AuthForms({ login, register }) {
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ email: "", password: "", name: "", phone: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setError(""); setBusy(true);
    try {
      if (mode === "login") await login(form.email, form.password);
      else await register(form);
    } catch (e) {
      const d = e?.response?.data?.detail;
      setError(typeof d === "string" ? d : "Une erreur est survenue. Réessayez.");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-md mx-auto">
      <h1 className="font-head font-bold text-3xl text-[#6f7d54] mb-1 text-center">{mode === "login" ? "Connexion" : "Créer un compte"}</h1>
      <p className="font-body text-sm text-[#3a4d68]/70 mb-6 text-center">Espace parents Island Camps</p>

      <button onClick={googleLogin} data-testid="google-login-btn" className="w-full flex items-center justify-center gap-3 rounded-full border-2 border-[#6f7d54]/15 bg-white font-bold text-[#3a4d68] py-3 mb-4 hover:border-[#e07a5f] transition-colors">
        <img src="https://www.google.com/favicon.ico" alt="" className="w-4 h-4" /> Continuer avec Google
      </button>

      <div className="flex items-center gap-3 my-4"><div className="flex-1 h-px bg-[#6f7d54]/15" /><span className="text-xs text-[#3a4d68]/50">ou</span><div className="flex-1 h-px bg-[#6f7d54]/15" /></div>

      <div className="space-y-3">
        {mode === "register" && (
          <>
            <input data-testid="acc-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nom du parent" className="w-full rounded-xl border border-[#6f7d54]/15 px-4 py-3 font-body" />
            <input data-testid="acc-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Téléphone (optionnel)" className="w-full rounded-xl border border-[#6f7d54]/15 px-4 py-3 font-body" />
          </>
        )}
        <input data-testid="acc-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="E-mail" className="w-full rounded-xl border border-[#6f7d54]/15 px-4 py-3 font-body" />
        <input data-testid="acc-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Mot de passe" className="w-full rounded-xl border border-[#6f7d54]/15 px-4 py-3 font-body" onKeyDown={(e) => e.key === "Enter" && submit()} />
        {error && <p data-testid="acc-error" className="text-[#b5232f] text-sm font-semibold">{error}</p>}
        <button onClick={submit} disabled={busy} data-testid="acc-submit" className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#e07a5f] hover:bg-[#cf6a50] disabled:opacity-50 text-white font-bold text-lg px-6 py-3.5 transition-colors">
          {busy ? <Loader2 className="animate-spin" size={20} /> : (mode === "login" ? <LogIn size={20} /> : <UserPlus size={20} />)}
          {mode === "login" ? "Se connecter" : "Créer mon compte"}
        </button>
      </div>

      <p className="text-center text-sm text-[#3a4d68]/80 mt-5">
        {mode === "login" ? "Pas encore de compte ? " : "Déjà un compte ? "}
        <button data-testid="acc-switch-mode" onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }} className="font-bold text-[#e07a5f]">
          {mode === "login" ? "Créer un compte" : "Se connecter"}
        </button>
      </p>
    </div>
  );
}

function Dashboard({ user, logout, authHeader, navigate }) {
  const [data, setData] = useState(null);
  const load = useCallback(() => {
    axios.get(`${API}/account/my-activity`, { headers: authHeader }).then((r) => setData(r.data)).catch(() => setData({ bookings: [], memberships: [] }));
  }, [authHeader]);
  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div className="flex items-center justify-between gap-4 flex-wrap mb-8">
        <div className="flex items-center gap-3">
          {user.picture ? <img src={user.picture} alt="" className="w-12 h-12 rounded-full object-cover" /> : <div className="w-12 h-12 rounded-full bg-[#6f7d54] text-white flex items-center justify-center font-head font-bold text-lg">{(user.name || user.email)[0]?.toUpperCase()}</div>}
          <div>
            <h1 className="font-head font-bold text-2xl text-[#6f7d54]" data-testid="account-name">Bonjour {user.name || user.email.split("@")[0]}</h1>
            <p className="font-body text-sm text-[#3a4d68]/70 flex items-center gap-1"><Mail size={13} /> {user.email}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Link to="/reservation" data-testid="account-book-btn" className="inline-flex items-center gap-1.5 rounded-full bg-[#e07a5f] text-white font-bold text-sm px-5 py-2.5">Réserver <ArrowRight size={16} /></Link>
          <button onClick={logout} data-testid="account-logout-btn" className="inline-flex items-center gap-1.5 rounded-full border border-[#6f7d54]/20 text-[#3a4d68] font-bold text-sm px-4 py-2.5"><LogOut size={15} /> Déconnexion</button>
        </div>
      </div>

      {!data ? <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#e07a5f]" /></div> : (
        <div className="grid md:grid-cols-2 gap-6">
          <section className="bg-white rounded-3xl border border-[#6f7d54]/10 p-6" data-testid="account-memberships">
            <h2 className="font-head font-bold text-lg text-[#6f7d54] flex items-center gap-2 mb-4"><BadgeCheck size={18} className="text-[#5e7d52]" /> Mon adhésion</h2>
            {data.memberships.length === 0 ? <p className="font-body text-sm text-[#3a4d68]/60">Aucune adhésion. <Link to="/adhesion" className="text-[#e07a5f] font-bold">Devenir adhérent</Link></p> : data.memberships.map((m) => (
              <div key={m.id} className="rounded-2xl bg-[#fbf6ec]/70 border border-[#6f7d54]/10 p-4 mb-2">
                <div className="flex items-center justify-between"><span className="font-bold text-[#6f7d54]">Adhésion {m.amount}€</span><span className={`text-xs font-bold rounded-full px-2.5 py-1 ${m.status === "paid" ? "bg-[#5e7d52]/15 text-[#4a6742]" : "bg-[#e0b36a]/20 text-[#8a6a1f]"}`}>{m.status === "paid" ? "Payée" : "En attente"}</span></div>
                <p className="text-sm text-[#3a4d68] mt-1">Valable jusqu'au {m.valid_until || "—"}</p>
              </div>
            ))}
          </section>

          <section className="bg-white rounded-3xl border border-[#6f7d54]/10 p-6" data-testid="account-bookings">
            <h2 className="font-head font-bold text-lg text-[#6f7d54] flex items-center gap-2 mb-4"><CalendarDays size={18} className="text-[#2e8b9e]" /> Mes réservations</h2>
            {data.bookings.length === 0 ? <p className="font-body text-sm text-[#3a4d68]/60">Aucune réservation pour le moment.</p> : data.bookings.map((b) => (
              <div key={b.id} className="rounded-2xl bg-[#fbf6ec]/70 border border-[#6f7d54]/10 p-4 mb-2">
                <div className="flex items-center justify-between"><span className="font-bold text-[#6f7d54]">{b.total_slots} session(s) · {b.amount}€</span><span className={`text-xs font-bold rounded-full px-2.5 py-1 ${b.status === "paid" ? "bg-[#5e7d52]/15 text-[#4a6742]" : "bg-[#e0b36a]/20 text-[#8a6a1f]"}`}>{b.status === "paid" ? "Payée" : b.status}</span></div>
                <p className="text-sm text-[#3a4d68] mt-1">{(b.created_at || "").slice(0, 10)}</p>
              </div>
            ))}
          </section>
        </div>
      )}

      <DocumentsSection authHeader={authHeader} />
    </div>
  );
}

function DocumentsSection({ authHeader }) {
  const [data, setData] = useState(null);
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = useCallback(() => {
    axios.get(`${API}/account/documents`, { headers: authHeader }).then((r) => setData(r.data)).catch(() => setData({ shared: [], mine: [] }));
  }, [authHeader]);
  useEffect(() => { load(); }, [load]);

  const upload = async () => {
    setErr("");
    if (!file) { setErr("Choisissez un fichier (PDF, JPG ou PNG)."); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", title || file.name);
      await axios.post(`${API}/account/documents`, fd, { headers: authHeader });
      setFile(null); setTitle(""); load();
    } catch (e) { setErr(e?.response?.data?.detail || "Échec de l'upload."); }
    setBusy(false);
  };
  const download = async (d) => {
    try {
      const r = await axios.get(`${API}/account/documents/${d.id}/download`, { headers: authHeader, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { /* ignore */ }
  };
  const del = async (id) => { if (!window.confirm("Supprimer ce document ?")) return; await axios.delete(`${API}/account/documents/${id}`, { headers: authHeader }); load(); };
  const fmtSize = (s) => (s > 1048576 ? `${(s / 1048576).toFixed(1)} Mo` : `${Math.max(1, Math.round(s / 1024))} Ko`);

  const DocRow = ({ d, canDelete }) => (
    <div className="flex items-center justify-between gap-3 rounded-2xl bg-[#fbf6ec]/70 border border-[#6f7d54]/10 p-3.5" data-testid={`doc-row-${d.id}`}>
      <div className="min-w-0 flex items-center gap-3">
        <FileText size={20} className="text-[#2e8b9e] shrink-0" />
        <div className="min-w-0">
          <div className="font-bold text-[#6f7d54] truncate">{d.title}</div>
          <div className="text-xs text-[#3a4d68]/70 truncate">{d.category ? `${d.category} · ` : ""}{d.original_filename} · {fmtSize(d.size)}</div>
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <button onClick={() => download(d)} data-testid={`doc-open-${d.id}`} className="inline-flex items-center gap-1.5 rounded-full bg-[#6f7d54] text-white font-bold text-xs px-3 py-2"><Download size={14} /> Ouvrir</button>
        {canDelete && <button onClick={() => del(d.id)} data-testid={`doc-delete-${d.id}`} className="text-[#e63946]"><Trash2 size={16} /></button>}
      </div>
    </div>
  );

  return (
    <section className="bg-white rounded-3xl border border-[#6f7d54]/10 p-6 mt-6" data-testid="account-documents">
      <h2 className="font-head font-bold text-lg text-[#6f7d54] flex items-center gap-2 mb-4"><FileText size={18} className="text-[#e07a5f]" /> Mes documents</h2>

      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <p className="font-body font-semibold text-[#3a4d68] mb-2 text-sm">Documents de l'association</p>
          {!data ? <Loader2 className="animate-spin text-[#e07a5f]" size={20} /> : data.shared.length === 0 ? <p className="text-sm text-[#3a4d68]/60">Aucun document disponible pour le moment.</p> : (
            <div className="space-y-2">{data.shared.map((d) => <DocRow key={d.id} d={d} canDelete={false} />)}</div>
          )}
        </div>
        <div>
          <p className="font-body font-semibold text-[#3a4d68] mb-2 text-sm">Mes dépôts</p>
          {!data ? null : data.mine.length === 0 ? <p className="text-sm text-[#3a4d68]/60 mb-3">Vous n'avez pas encore déposé de document.</p> : (
            <div className="space-y-2 mb-3">{data.mine.map((d) => <DocRow key={d.id} d={d} canDelete />)}</div>
          )}
          <div className="rounded-2xl border border-dashed border-[#6f7d54]/25 p-3.5">
            <input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="account-doc-title" placeholder="Titre (ex. Fiche remplie)" className="w-full rounded-xl border border-[#6f7d54]/15 px-3 py-2 text-sm mb-2" />
            <input type="file" accept="application/pdf,image/jpeg,image/png" data-testid="account-doc-file" onChange={(e) => setFile(e.target.files?.[0] || null)} className="block text-sm" />
            {err && <p className="text-[#b5232f] text-sm font-semibold mt-2">{err}</p>}
            <button onClick={upload} disabled={busy} data-testid="account-doc-upload" className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-[#e07a5f] text-white font-bold text-sm px-4 py-2 disabled:opacity-50">{busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Déposer</button>
          </div>
        </div>
      </div>
    </section>
  );
}
